import React, { useState, useEffect } from "react";
import {
  Box, TextField, Button, Typography, Paper, Table, TableHead, 
  TableRow, TableCell, TableBody, Card, Grid, CircularProgress, MenuItem, Select, FormControl, InputLabel
} from "@mui/material";
import { db } from "../../firebase";
import { collection, getDocs, query, where, onSnapshot } from "firebase/firestore";

function Salaeryreport() {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [staffList, setStaffList] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const years = [2024, 2025, 2026, 2027];

  // 1. Load Staff names to map IDs to Names
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "staff"), (snap) => {
      setStaffList(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const fetchSalaryReport = async () => {
    setLoading(true);
    try {
      // Query monthly payments for selected month and year
      const q = query(
        collection(db, "monthlypayments"),
        where("month", "==", Number(month)),
        where("year", "==", Number(year))
      );

      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReportData(data);
    } catch (error) {
      console.error("Error fetching salary report:", error);
      alert("Error loading data. Check console.");
    } finally {
      setLoading(false);
    }
  };

  const getStaffName = (id) => {
    const staff = staffList.find(s => s.id === id);
    return staff ? staff.name : "Unknown Staff";
  };

  const totalPaid = reportData.reduce((sum, item) => sum + (Number(item.netPaid) || 0), 0);

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Monthly Salary Report
      </Typography>

      <Card sx={{ p: 2, mb: 3 }} className="no-print">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                {months.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button variant="contained" onClick={fetchSalaryReport} disabled={loading} sx={{ mr: 1 }}>
              {loading ? <CircularProgress size={24} /> : "View Report"}
            </Button>
            <Button variant="outlined" onClick={() => window.print()}>Print Report</Button>
          </Grid>
        </Grid>
      </Card>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '1px solid #ddd' }}>
        <Box p={2} sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Payroll Summary: {months[month - 1]} {year}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#eeeeee" }}>
            <TableRow>
              <TableCell><strong>Staff Name</strong></TableCell>
              <TableCell><strong>Base Salary</strong></TableCell>
              <TableCell><strong>Attendance Earned</strong></TableCell>
              <TableCell><strong>Advance Deduction</strong></TableCell>
              <TableCell><strong>Net Paid</strong></TableCell>
              <TableCell><strong>Date Paid</strong></TableCell>
              <TableCell><strong>Method</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell><strong>{getStaffName(row.staffId)}</strong></TableCell>
                <TableCell>₹{row.baseSalary}</TableCell>
                <TableCell>₹{row.attendanceEarnings}</TableCell>
                <TableCell sx={{ color: 'error.main' }}>
                   {row.advanceDeduction > 0 ? `-₹${row.advanceDeduction}` : "₹0"}
                </TableCell>
                <TableCell><strong>₹{row.netPaid}</strong></TableCell>
                <TableCell>{row.payDate}</TableCell>
                <TableCell>{row.method}</TableCell>
              </TableRow>
            ))}
            {reportData.length > 0 && (
              <TableRow sx={{ bgcolor: "#f0f0f0" }}>
                <TableCell colSpan={4} align="right"><strong>Total Outflow:</strong></TableCell>
                <TableCell colSpan={3}><strong>₹{totalPaid.toLocaleString()}</strong></TableCell>
              </TableRow>
            )}
            {reportData.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  No salary records found for the selected period.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { padding: 0; margin: 0; }
        }
      `}</style>
    </Box>
  );
}

export default Salaeryreport;